/**
 * Typed model of the global header's real presentation axes. Phase 1 of the
 * header-config refactor: GlobalHeader maps its 13 legacy boolean props onto
 * this config internally (pure refactor, no behavior change). Later phases let
 * pages declare a config/preset directly and retire the route lists in
 * ApplicationLayout.
 *
 * The 2FA lockdown on /security is intentionally NOT part of this config — it
 * is a security behavior computed inside GlobalHeader from the session cookie
 * and must not be page-overridable.
 */

type THeaderSurface = 'default' | 'transparent' | 'transparent-dark' | 'white';

/**
 * - `contained` — grid container outer, `container-main` inner (most pages)
 * - `fluid` — no outer constraint, slim inner margins (SERP, checkout, locality, wishlists)
 * - `wide` — `max-w-xxxxl` padded outer, bare inner (home, RV delivery)
 * - `wide-contained` — `wide` outer with `container-main` inner (promo pages)
 */
type THeaderWidth = 'contained' | 'fluid' | 'wide' | 'wide-contained';

export type THeaderCenter =
  | { type: 'none' }
  | { type: 'tabs' }
  | {
      type: 'search';
      serp?: boolean;
      campground?: boolean;
      /** Render the header search as the SERP pill even off the search page (listing pages). */
      forceSearchPageMode?: boolean;
      /** Pill edits update in place; navigation to search waits for an explicit submit. */
      deferNavigationUntilSubmit?: boolean;
    };

export interface IHeaderConfig {
  center: THeaderCenter;
  surface: THeaderSurface;
  width: THeaderWidth;
  /** Header is sticky at the top of the viewport (drives the stuck-to-top scroll listener). */
  sticky: boolean;
  /** Animate background-color changes (home / RV-delivery scroll-driven backgrounds). */
  animateSurface: boolean;
}

export interface ILegacyHeaderProps {
  isHomePage?: boolean;
  noSearchNavbar?: boolean;
  isFluidPage?: boolean;
  isTransparent?: boolean;
  isSticky?: boolean;
  isSERP?: boolean;
  isHeaderWhite?: boolean;
  isDarkNavigation?: boolean;
  isRvDeliveryPage?: boolean;
  isNewPromoPage?: boolean;
  isRVListingPage?: boolean;
  deferSearchNavigationUntilSubmit?: boolean;
  isCampgroundSearchPage?: boolean;
}

/**
 * Maps the legacy boolean props computed by ApplicationLayout onto the header
 * config. Encodes the precedence the old inline conditionals expressed:
 * tabs (home / RV delivery) win over no-search; white wins over transparent;
 * wide wins over fluid. Impossible combinations (white+transparent,
 * promo+fluid) don't occur on any current route — the winner here matches the
 * old class precedence for the combinations that do.
 */
export const legacyPropsToHeaderConfig = ({
  isHomePage,
  noSearchNavbar,
  isFluidPage,
  isTransparent,
  isSticky,
  isSERP,
  isHeaderWhite,
  isDarkNavigation,
  isRvDeliveryPage,
  isNewPromoPage,
  isRVListingPage,
  deferSearchNavigationUntilSubmit,
  isCampgroundSearchPage,
}: ILegacyHeaderProps): IHeaderConfig => {
  let center: THeaderCenter;
  if (isHomePage || isRvDeliveryPage) {
    center = { type: 'tabs' };
  } else if (noSearchNavbar) {
    center = { type: 'none' };
  } else {
    center = {
      type: 'search',
      serp: isSERP,
      campground: isCampgroundSearchPage,
      forceSearchPageMode: isRVListingPage,
      deferNavigationUntilSubmit: deferSearchNavigationUntilSubmit,
    };
  }

  let surface: THeaderSurface = 'default';
  if (isHeaderWhite) {
    surface = 'white';
  } else if (isTransparent) {
    surface = isDarkNavigation ? 'transparent-dark' : 'transparent';
  }

  let width: THeaderWidth = 'contained';
  if (isHomePage || isRvDeliveryPage) {
    width = 'wide';
  } else if (isNewPromoPage) {
    width = 'wide-contained';
  } else if (isFluidPage) {
    width = 'fluid';
  }

  return {
    center,
    surface,
    width,
    sticky: Boolean(isSticky),
    animateSurface: Boolean(isHomePage || isRvDeliveryPage),
  };
};

/* ------------------------------------------------------------------------- *
 * Phase 2 — per-page header declarations.
 *
 * Pages opt in with a static: `Page.header = { preset: 'white' }`. _app passes
 * `Component.header` into ApplicationLayout, which uses the preset's
 * page-identity facts instead of matching router.pathname against route
 * lists. Runtime facts (SERP, breakpoints, rental category, 2FA) remain
 * computed — a preset only declares what is true of the page itself.
 * ------------------------------------------------------------------------- */

type THeaderPresetName =
  | 'default'
  | 'white'
  | 'gift-cards'
  | 'seasonal-guide'
  | 'serp'
  | 'search'
  | 'listing'
  | 'locality'
  | 'checkout'
  | 'mobile-hidden'
  | 'hidden'
  | 'fluid';

interface IPageHeaderDeclaration {
  preset: THeaderPresetName;
  /**
   * Modifiers a page may add on top of its preset. Deliberately limited to
   * these three: each was previously its own preset (`serp-campground`,
   * `listing-mobile-hidden`, `contact-host`), which meant a preset could never
   * accumulate users and the table grew one entry per combination.
   *
   * They are additive only — a modifier can turn a fact on, never off — and the
   * list is intentionally closed. Surface, width, and center stay preset-only:
   * those are the axes whose free combination produced the original mess.
   */
  /** Point the search center at campground search instead of RV search. */
  campground?: boolean;
  /** Hide header and footer below the `md` breakpoint. */
  hiddenMobile?: boolean;
  /** Hide the footer at every breakpoint. */
  hideFooter?: boolean;
}

/** Intersect with NextPage to attach the static: `const Page: NextPage & TPageWithHeader`. */
export type TPageWithHeader = { header?: IPageHeaderDeclaration };

/** Page-identity facts a preset contributes. All optional; absent means false. */
interface IPageHeaderFacts {
  /** Transparent header overlaying the page hero, all breakpoints. */
  transparent?: boolean;
  /** Transparent on desktop only (mobile keeps a painted header). */
  transparentDesktopOnly?: boolean;
  /** Dark logo/text on the transparent desktop header. */
  darkNavigationDesktop?: boolean;
  /** White header, all breakpoints. */
  white?: boolean;
  /** White header on mobile only. */
  whiteMobileOnly?: boolean;
  /** Full-bleed page — header uses slim margins instead of the main container. */
  fluid?: boolean;
  /** Header scrolls away with the page instead of sticking. */
  nonSticky?: boolean;
  /**
   * What renders in the header's center slot. Absent means 'none'.
   * - `search` — the header search bar (base search pages like /service-members)
   * - `serp` — search bar in SERP mode
   * - `listing-pill` / `locality-pill` — SERP pill whose edits update in place;
   *   navigating to search waits for an explicit Search submit rather than
   *   firing on every field-confirm event.
   */
  center?: 'none' | 'search' | 'serp' | 'listing-pill' | 'locality-pill';
  /** The search center targets campground search instead of RV search. */
  campgroundSearch?: boolean;
  /** Hide header and footer entirely (immersive pages: photos, signing, guided search). */
  hidden?: boolean;
  /** Hide header and footer below the md breakpoint only. */
  hiddenMobile?: boolean;
  /** Hide only the footer (checkout, contact-host). */
  hideFooter?: boolean;
  /** Desktop sticky starts at lg instead of md (checkout keeps tablets unstuck). */
  stickyFromLg?: boolean;
  /** On mobile the header is always sticky (SERP/locality, RV category only). */
  mobileAlwaysSticky?: boolean;
}

const HEADER_PRESETS: Record<THeaderPresetName, IPageHeaderFacts> = {
  default: {},
  white: { white: true },
  'gift-cards': {
    transparentDesktopOnly: true,
    darkNavigationDesktop: true,
    whiteMobileOnly: true,
    nonSticky: true,
  },
  'seasonal-guide': { transparent: true, fluid: true, nonSticky: true },
  serp: { center: 'serp', fluid: true, mobileAlwaysSticky: true },
  search: { center: 'search' },
  listing: { center: 'listing-pill', nonSticky: true },
  locality: { center: 'locality-pill', fluid: true, mobileAlwaysSticky: true },
  checkout: { hideFooter: true, stickyFromLg: true },
  'mobile-hidden': { hiddenMobile: true },
  hidden: { hidden: true },
  fluid: { fluid: true },
};

interface IResolvedPageHeaderFacts {
  headerIsTransparent: boolean;
  headerDesktopTransparent: boolean;
  isDarkNavigation: boolean;
  isHeaderWhite: boolean;
  isFluidPage: boolean;
  nonStickyHeader: boolean;
  hideHeaderAndFooter: boolean;
  mobileHideHeaderAndFooter: boolean;
  hideFooter: boolean;
  stickyFromLg: boolean;
  mobileAlwaysSticky: boolean;
  isSERP: boolean;
  noSearchNavbar: boolean;
  isRVListingPage: boolean;
  deferSearchNavigationUntilSubmit: boolean;
  isCampgroundSearchPage: boolean;
}

/**
 * Resolves a page's declared preset to the booleans ApplicationLayout used to
 * derive from its route lists. Returns concrete booleans (never undefined) so
 * the props handed to GlobalHeader are identical to the legacy computation.
 */
export const resolvePageHeaderFacts = (
  { preset, campground, hiddenMobile, hideFooter }: IPageHeaderDeclaration,
  { isMobile }: { isMobile: boolean },
): IResolvedPageHeaderFacts => {
  const presetFacts = HEADER_PRESETS[preset];

  // TypeScript catches typos in .tsx pages, but the locality pages are .jsx —
  // an unrecognized preset there would otherwise ship a default header with no
  // signal. Fail loudly in dev/test; degrade to the default in production
  // rather than blanking the header on every page.
  if (!presetFacts) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(
        `Unknown header preset "${preset}". Valid presets: ${Object.keys(HEADER_PRESETS).join(', ')}`,
      );
    }
    return resolvePageHeaderFacts({ preset: 'default' }, { isMobile });
  }

  // Page modifiers layer on top of the preset. Additive only, so a preset's own
  // facts can never be switched off by a page.
  const facts: IPageHeaderFacts = {
    ...presetFacts,
    campgroundSearch: presetFacts.campgroundSearch || campground,
    hiddenMobile: presetFacts.hiddenMobile || hiddenMobile,
    hideFooter: presetFacts.hideFooter || hideFooter,
  };

  const center = facts.center ?? 'none';

  return {
    headerIsTransparent: Boolean(facts.transparent),
    headerDesktopTransparent: Boolean(!isMobile && facts.transparentDesktopOnly),
    isDarkNavigation: Boolean(!isMobile && facts.darkNavigationDesktop),
    isHeaderWhite: Boolean(facts.white || (isMobile && facts.whiteMobileOnly)),
    isFluidPage: Boolean(facts.fluid),
    nonStickyHeader: Boolean(facts.nonSticky),
    hideHeaderAndFooter: Boolean(facts.hidden),
    mobileHideHeaderAndFooter: Boolean(facts.hiddenMobile),
    hideFooter: Boolean(facts.hideFooter),
    stickyFromLg: Boolean(facts.stickyFromLg),
    mobileAlwaysSticky: Boolean(facts.mobileAlwaysSticky),
    isSERP: center === 'serp',
    noSearchNavbar: center === 'none',
    isRVListingPage: center === 'listing-pill',
    deferSearchNavigationUntilSubmit: center === 'listing-pill' || center === 'locality-pill',
    isCampgroundSearchPage: Boolean(facts.campgroundSearch),
  };
};
